import { useTranslation } from '@/i18n'
import type { EvidenceRow, Period, RunState } from '../v4/contracts'
import { OPEN_LIST_MAX, summaryText, unknownLabel } from '../v4/answer-text'
import { valueText } from './evidenceText'
import { canRerun } from './runRules'
import RerunBar from './RerunBar'

const anchor = (id: string) => `ev-${id.replace(/[^\w-]/g, '_')}`
const sourceText = (x: EvidenceRow['source']) => `${x.kind} ${x.ref}${x.locator ? ` (${x.locator})` : ''}`

/**
 * The Report tab over a v4 run: the engine's answer (headline, the rest of its prose, the findings
 * with the evidence each cites, the actions, what stayed unknown, the sources), the cited evidence
 * rows, and, for a finished run with an approved plan, the rerun control (the same plan on a new
 * period, as a new run). Ported from the v4 page's answer panel into the old report's chrome.
 */
export default function RunReport({
  run,
  busy,
  onRerun,
}: {
  run: RunState
  busy: boolean
  onRerun: (period: Period) => Promise<unknown>
}) {
  const { t } = useTranslation()
  const a = run.trace.answer
  const rows = new Map(run.trace.evidence.map((e) => [e.id, e]))
  const cited = a
    ? [...new Set(a.findings.flatMap((f) => f.evidenceRefs))].map((id) => rows.get(id)).filter((e): e is EvidenceRow => !!e)
    : []
  const subjects = (refs: string[]) =>
    [...new Set(refs.map((id) => rows.get(id)).filter(Boolean).map((e) => e!.subject.display ?? e!.subject.id))]
  const rerunnable = canRerun(run)

  if (!a) {
    return (
      <div className="mx-auto max-w-3xl space-y-4 p-6">
        <p className="text-sm text-muted-foreground">
          {t('workflow:builder.report_pending', { defaultValue: 'The answer appears here when the run finishes.' })}
        </p>
        {rerunnable && <RerunBar run={run} busy={busy} onRerun={onRerun} />}
      </div>
    )
  }
  const summary = summaryText(a.headline, a.prose)
  return (
    <article className="mx-auto max-w-3xl space-y-5 p-6">
      <header className="space-y-2">
        <h2 className="text-lg font-semibold text-foreground">{a.headline}</h2>
        {summary && <p className="whitespace-pre-wrap text-sm text-foreground">{summary}</p>}
      </header>

      {a.findings.length > 0 && (
        <section className="space-y-2">
          <h3 className="text-sm font-medium text-foreground">
            {t('workflow:report.findings', { defaultValue: 'Findings' })} ({a.findings.length})
          </h3>
          <ol className="space-y-2">
            {a.findings.map((f, i) => (
              <li key={f.id} className="rounded-xl border border-border bg-background p-3 text-sm dark:bg-dark-card">
                <p className="text-foreground">
                  {f.rank ? `${f.rank}. ` : ''}
                  {f.statement}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {t('workflow:report.finding_n', { defaultValue: 'finding {{n}}', n: i + 1 })} ·{' '}
                  {f.evidenceRefs.map((id, j) => (
                    <span key={id}>
                      {j ? ' · ' : ''}
                      <a className="underline" href={`#${anchor(id)}`}>
                        {id}
                      </a>
                    </span>
                  ))}
                  {subjects(f.evidenceRefs).length > 0 && ` · ${subjects(f.evidenceRefs).join(', ')}`}
                </p>
              </li>
            ))}
          </ol>
        </section>
      )}

      {a.actions.length > 0 && (
        <section className="space-y-2">
          <h3 className="text-sm font-medium text-foreground">{t('workflow:report.actions', { defaultValue: 'Actions' })}</h3>
          <ul className="space-y-2">
            {a.actions.map((x) => (
              <li key={x.id} className="rounded-xl border border-border bg-background p-3 text-sm text-foreground dark:bg-dark-card">
                {x.statement}
                <span className="block text-xs text-muted-foreground">
                  {t('workflow:report.cites', { defaultValue: 'cites' })}{' '}
                  {x.findingRefs.map((ref) => `${t('workflow:report.finding_n', { defaultValue: 'finding {{n}}', n: a.findings.findIndex((f) => f.id === ref) + 1 || ref })}`).join(', ')}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {a.unknowns.length > 0 && (
        <section className="space-y-2">
          <h3 className="text-sm font-medium text-foreground">{t('workflow:report.unknowns', { defaultValue: 'Still unknown' })}</h3>
          <ul className="space-y-1 text-sm text-foreground">
            {a.unknowns.map((u, i) => (
              <li key={i}>
                <b>{unknownLabel(u.kind)}</b>: {u.detail}
              </li>
            ))}
          </ul>
        </section>
      )}

      {a.sources.length > 0 && (
        <p className="text-xs text-muted-foreground">
          {t('workflow:report.sources', { defaultValue: 'Sources' })}:{' '}
          {a.sources.map((x, i) => {
            const row = cited.find((e) => e.source.kind === x.kind && e.source.ref === x.ref && (x.locator === undefined || e.source.locator === x.locator))
            return (
              <span key={i}>
                {i ? ' · ' : ''}
                {row ? <a className="underline" href={`#${anchor(row.id)}`}>{sourceText(x)}</a> : sourceText(x)}
              </span>
            )
          })}
        </p>
      )}

      {cited.length > 0 && (
        <details open={cited.length <= OPEN_LIST_MAX} className="rounded-xl border border-border bg-background p-3 text-sm dark:bg-dark-card">
          <summary className="cursor-pointer font-medium text-foreground">
            {t('workflow:report.evidence', { defaultValue: 'Evidence rows' })} ({cited.length})
          </summary>
          <ul className="mt-2 space-y-1">
            {cited.map((e) => (
              <li key={e.id} id={anchor(e.id)} className="text-foreground">
                <span className="font-mono text-xs text-muted-foreground">{e.id}</span> {e.subject.display ?? e.subject.id}: {valueText(e, e.outcome)}
                {e.at ? ` at ${e.at.slice(0, 16)}` : ''} <span className="text-xs text-muted-foreground">· {sourceText(e.source)}</span>
              </li>
            ))}
          </ul>
        </details>
      )}

      {rerunnable && <RerunBar run={run} busy={busy} onRerun={onRerun} />}
    </article>
  )
}
